import {useContext, useEffect, useState} from 'react';
import AppContext from "../../../context";

import Data from "../../../repository";
import Loading from "../../components/Loading";

import styles from '../../../styles/CurrentMembership.module.scss';
import moment from "moment";

function WorldpayCurrentMembership() {
    const context = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [subscriptions, setSubscriptions] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    useEffect(async () => {
        await loadSubscriptions();
    }, [])

    const loadSubscriptions = async () => {
        setLoading(true);
        const result = await Data.fetchWorldpaySubscriptions();
        const membershipSubs = result.data.filter(sub => sub.type === 'membership');
        setSubscriptions(membershipSubs);
        setLoading(false);
    }

    const handlePause = async (subscriptionId) => {
        setActionLoading(true);
        try {
            await Data.pauseWorldpaySubscription(subscriptionId);
            await loadSubscriptions();
        } catch (err) {
            alert('Failed to pause subscription. Please try again.');
        }
        setActionLoading(false);
    }

    const handleResume = async (subscriptionId) => {
        setActionLoading(true);
        try {
            await Data.resumeWorldpaySubscription(subscriptionId);
            await loadSubscriptions();
        } catch (err) {
            alert('Failed to resume subscription. Please try again.');
        }
        setActionLoading(false);
    }

    const handleUpdatePaymentMethod = async (subscriptionId) => {
        setUpdateLoading(true);
        setUpdateError(null);
        try {
            const result = await Data.initiateWorldpayPaymentUpdate(subscriptionId);
            window.location.href = result.data.url;
        } catch (err) {
            setUpdateError('Could not start payment update. Please try again.');
            setUpdateLoading(false);
        }
    }

    const handleCancel = async (subscriptionId, immediate = false) => {
        const confirmMsg = immediate
            ? 'Are you sure you want to cancel your membership immediately? Your access will end today.'
            : 'Are you sure you want to cancel your membership? Your access will continue until the end of your current billing period.';

        if (!window.confirm(confirmMsg)) return;

        setActionLoading(true);
        try {
            await Data.cancelWorldpaySubscription(subscriptionId, immediate);
            await loadSubscriptions();
        } catch (err) {
            alert('Failed to cancel subscription. Please try again.');
        }
        setActionLoading(false);
    }

    if (loading) return <section><Loading button={false} /></section>

    const currentSubscription = subscriptions[subscriptions.length - 1];

    if (!currentSubscription) {
        return <section><p>No active membership found.</p></section>;
    }

    const plan = currentSubscription.MembershipPlan;

    const renderStatus = () => {
        // Check if subscription is cancelled but still active until period end
        if (currentSubscription.status === 'active' && currentSubscription.cancelAtPeriodEnd) {
            return (
                <p>Your membership has been <strong>cancelled</strong> and will remain active until <strong>{moment(currentSubscription.currentPeriodEnd).format('Do MMMM')}</strong>.</p>
            );
        }

        switch (currentSubscription.status) {
            case 'active':
                return (
                    <p>Your membership is currently <strong>active</strong> and will renew on <strong>{moment(currentSubscription.nextBillingDate).format('Do MMMM')}</strong>.</p>
                );
            case 'paused':
                return (
                    <>
                        <p>Your membership is currently <strong>paused</strong>. Your access remains active until <strong>{moment(currentSubscription.currentPeriodEnd).format('Do MMMM')}</strong>.</p>
                        <button
                            className={'btn btnPrimary'}
                            onClick={() => handleResume(currentSubscription._id)}
                            disabled={actionLoading}
                        >
                            Resume Membership
                        </button>
                    </>
                );
            case 'past_due':
                return (
                    <>
                        <p>Your latest <strong>payment has failed</strong>. Your grace period ends on <strong>{moment(currentSubscription.gracePeriodEnds).format('Do MMMM')}</strong>.</p>
                        <p className={styles.warning}>Please update your payment method below to avoid losing access.</p>
                    </>
                );
            case 'pending_cancellation':
                return (
                    <p>Your membership has been <strong>cancelled</strong> and will remain active until <strong>{moment(currentSubscription.currentPeriodEnd).format('Do MMMM')}</strong>.</p>
                );
            case 'cancelled':
                return (
                    <p>Your membership has been <strong>cancelled</strong>{currentSubscription.cancelledAt ? ` on ${moment(currentSubscription.cancelledAt).format('Do MMMM YYYY')}` : ''}.</p>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <section className={styles.status}>
                {renderStatus()}
            </section>

            <section>
                <h2>Membership Plan</h2>

                <div className={styles.plan}>
                    <div className={styles.header}>
                        <h3>{plan?.name}</h3>
                        <div className={styles.price}>
                            <h2>£{(currentSubscription.amount / 100).toFixed(2)}</h2>
                            <p>/ {currentSubscription.intervalCount} {currentSubscription.interval}{currentSubscription.intervalCount > 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    <p>{plan?.description}</p>
                </div>

                {currentSubscription.status === 'active' && !currentSubscription.cancelAtPeriodEnd && (
                    <div className={styles.actions}>
                        <button
                            className={'btn btnEmpty'}
                            onClick={() => handlePause(currentSubscription._id)}
                            disabled={actionLoading}
                        >
                            Pause Membership
                        </button>
                        <button
                            className={'btn btnEmpty btnDanger'}
                            onClick={() => handleCancel(currentSubscription._id, false)}
                            disabled={actionLoading}
                        >
                            Cancel Membership
                        </button>
                    </div>
                )}
            </section>

            <section>
                <h2>Payment Method</h2>

                <div className={styles.paymentMethod}>
                    <div>
                        <p>Card Type</p>
                        <p>{currentSubscription.cardBrand || 'Card'}</p>
                    </div>
                    <div>
                        <p>Card Number</p>
                        <p>**** **** **** {currentSubscription.cardLastFour || '****'}</p>
                    </div>
                </div>

                {updateError && <p style={{ color: '#c0392b', marginTop: 8 }}>{updateError}</p>}
                {['active', 'past_due', 'paused'].includes(currentSubscription.status) && (
                    <button
                        className={'btn btnEmpty'}
                        onClick={() => handleUpdatePaymentMethod(currentSubscription._id)}
                        disabled={updateLoading}
                        style={{ marginTop: 16 }}
                    >
                        {updateLoading ? 'Redirecting...' : 'Update Payment Method'}
                    </button>
                )}
            </section>
        </>
    );
}

export default WorldpayCurrentMembership;
